import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import { SeatCard, SeatData } from './SeatCard';
import { useToast } from './Toast';
import {
  TEXT_DARK, TEXT_SECONDARY, TEXT_MUTED, FONT_HEADING,
  BG_CARD, BORDER_LIGHT, ZONE_COLORS,
} from './designTokens';

interface TableDetailData {
  id: string;
  tableNumber: number;
  tableName: string;
  zone: { name: string; colorCode: string };
  capacity: number;
  status: string;
  notes: string | null;
  reservedGroup: string | null;
  blockCategory: string | null;
  isAccessible: boolean;
  seats: (SeatData & {
    guestTicketNumber?: string | null;
    guestTier?: string | null;
    guestMeal?: string | null;
    guestChapter?: string | null;
    guestFamily?: string | null;
    guestVip?: boolean;
  })[];
}

interface TableDetailProps {
  tableId: string;
  onClose: () => void;
  onAssignSeat?: (seatId: string) => void;
  onBlockSeat?: (seatId: string) => void;
  onHoldSeat?: (seatId: string) => void;
  onMutationSuccess?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  panel: {
    width: '420px',
    maxWidth: '90vw',
    backgroundColor: BG_CARD,
    height: '100vh',
    maxHeight: '100%',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
    padding: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  title: {
    fontSize: '1.2rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: TEXT_MUTED,
    marginTop: '0.2rem',
  },
  closeBtn: {
    padding: '0.3rem 0.6rem',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: TEXT_MUTED,
    borderRadius: '4px',
  },
  meta: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    marginBottom: '1rem',
  },
  metaItem: {
    fontSize: '0.78rem',
    color: TEXT_SECONDARY,
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  zoneBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.2rem 0.5rem',
    borderRadius: '4px',
    color: '#fff',
  },
  statusBadge: {
    fontSize: '0.72rem',
    fontWeight: 600,
    padding: '0.15rem 0.45rem',
    borderRadius: '3px',
    backgroundColor: '#eceff1',
    color: TEXT_DARK,
  },
  seatsList: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_SECONDARY,
    fontSize: '0.9rem',
  },
  error: {
    color: '#b71c1c',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  notes: {
    fontSize: '0.78rem',
    color: TEXT_SECONDARY,
    backgroundColor: '#f9faf9',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontStyle: 'italic',
  },
  accessibleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#00695c',
    backgroundColor: '#e0f2f1',
    padding: '0.15rem 0.4rem',
    borderRadius: '3px',
  },
};

export function TableDetail({ tableId, onClose, onAssignSeat, onBlockSeat, onHoldSeat, onMutationSuccess }: TableDetailProps) {
  const [table, setTable] = useState<TableDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const fetchTable = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<TableDetailData>(`/table-management/tables/${tableId}`);
      setTable(data);
    } catch {
      setError('Failed to load table details.');
    } finally {
      setLoading(false);
    }
  }, [tableId]);

  useEffect(() => {
    fetchTable();
  }, [fetchTable]);

  const handleUnassign = async (seatId: string) => {
    // Optimistic update: immediately show seat as Available
    const previousTable = table;
    if (table) {
      setTable({
        ...table,
        seats: table.seats.map((s) =>
          s.id === seatId
            ? { ...s, status: 'Available', guestId: null, guestName: undefined, checkedIn: false, checkedInAt: null }
            : s
        ),
      });
    }

    try {
      await apiClient(`/table-management/seats/${seatId}/unassign`, { method: 'POST' });
      showToast('success', 'Seat unassigned.');
      onMutationSuccess?.();
      fetchTable(); // Refresh with server truth
    } catch (err: unknown) {
      // Rollback optimistic update
      setTable(previousTable);
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr?.statusCode === 409) {
        showToast('error', 'Conflict detected. Refreshing data...');
        fetchTable();
        onMutationSuccess?.();
      } else {
        showToast('error', apiErr?.message || 'Failed to unassign seat.');
      }
    }
  };

  const handleReleaseSeat = async (seatId: string) => {
    try {
      await apiClient(`/table-management/seats/${seatId}/release-hold`, { method: 'POST' });
      showToast('success', 'Seat hold released.');
      onMutationSuccess?.();
      fetchTable();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      showToast('error', apiErr?.message || 'Failed to release seat hold.');
    }
  };

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      style={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={table ? `Table ${table.tableNumber} detail` : 'Table detail'}
    >
      <div style={styles.panel}>
        {loading && <div style={styles.loading}>Loading table details...</div>}

        {error && (
          <div style={styles.error} role="alert">
            {error}
          </div>
        )}

        {table && (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>
                  Table {table.tableNumber} — {table.tableName}
                </h2>
                <div style={styles.subtitle}>
                  {table.seats.filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn').length} / {table.capacity} seats occupied
                </div>
              </div>
              <button
                style={styles.closeBtn}
                onClick={onClose}
                aria-label="Close table detail panel"
              >
                ✕
              </button>
            </div>

            <div style={styles.meta}>
              <span
                style={{ ...styles.zoneBadge, backgroundColor: ZONE_COLORS[table.zone.name] || table.zone.colorCode }}
              >
                {table.zone.name}
              </span>
              <span style={styles.statusBadge}>{table.status}</span>
              {table.isAccessible && (
                <span style={styles.accessibleBadge}>♿ Accessible</span>
              )}
              {table.reservedGroup && (
                <span style={styles.metaItem}>
                  Reserved: {table.reservedGroup}
                  {table.blockCategory && ` (${table.blockCategory})`}
                </span>
              )}
            </div>

            {table.notes && <div style={styles.notes}>{table.notes}</div>}

            {/* Table-level Hold/Block actions */}
            <div style={{ display: 'flex', gap: '0.5rem', margin: '0.75rem 0' }}>
              {(table.status === 'Available' || table.status === 'PartiallyReserved') && (
                <button
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#e65100', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={async () => {
                    const reason = window.prompt('Hold reason (optional):');
                    try {
                      await apiClient(`/table-management/tables/${tableId}/full-block`, {
                        method: 'POST',
                        body: { blockCategory: 'Hold', reservedGroup: reason || undefined },
                      });
                      showToast('success', `Table ${table.tableNumber} placed on hold.`);
                      fetchTable();
                      onMutationSuccess?.();
                    } catch (err: any) {
                      showToast('error', err?.message || 'Failed to hold table.');
                    }
                  }}
                  aria-label="Hold entire table"
                >
                  🔒 Hold Table
                </button>
              )}
              {(table.status === 'Available' || table.status === 'PartiallyReserved') && (
                <button
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={async () => {
                    const reason = window.prompt('Block reason (optional):');
                    try {
                      await apiClient(`/table-management/tables/${tableId}/full-block`, {
                        method: 'POST',
                        body: { blockCategory: 'Block', reservedGroup: reason || undefined },
                      });
                      showToast('success', `Table ${table.tableNumber} blocked.`);
                      fetchTable();
                      onMutationSuccess?.();
                    } catch (err: any) {
                      showToast('error', err?.message || 'Failed to block table.');
                    }
                  }}
                  aria-label="Block entire table"
                >
                  🚫 Block Table
                </button>
              )}
              {(table.status === 'Held' || table.status === 'Blocked') && (
                <button
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={async () => {
                    try {
                      await apiClient(`/table-management/tables/${tableId}/full-block`, { method: 'DELETE' });
                      showToast('success', `Table ${table.tableNumber} released.`);
                      fetchTable();
                      onMutationSuccess?.();
                    } catch (err: any) {
                      showToast('error', err?.message || 'Failed to release table.');
                    }
                  }}
                  aria-label="Release table hold/block"
                >
                  ✅ Release Table
                </button>
              )}
              {table.seats.some((s) => s.status === 'Held') && (
                <button
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={async () => {
                    const heldCount = table.seats.filter((s) => s.status === 'Held').length;
                    if (!window.confirm(`Release all ${heldCount} held seat(s) on this table?`)) return;
                    try {
                      await apiClient(`/table-management/tables/${tableId}/release-all-holds`, { method: 'POST' });
                      showToast('success', `Released ${heldCount} held seat(s) on table ${table.tableNumber}.`);
                      fetchTable();
                      onMutationSuccess?.();
                    } catch (err: any) {
                      showToast('error', err?.message || 'Failed to release held seats.');
                    }
                  }}
                  aria-label={`Release all held seats on table ${table.tableNumber}`}
                >
                  🔓 Release All Held
                </button>
              )}
            </div>

            {/* Guest List with Print — shown above seat cards */}
            {(() => {
              const occupiedSeats = table.seats.filter(
                (s) => s.status === 'Assigned' || s.status === 'CheckedIn'
              );
              if (occupiedSeats.length === 0) return null;

              const handlePrintTable = () => {
                const headerCells = ['Seat', 'Name', 'Ticket #', 'Tier', 'Meal', 'Chapter', 'Family', 'VIP']
                  .map((h) => `<th>${h}</th>`)
                  .join('');
                const rows = occupiedSeats
                  .sort((a, b) => a.seatNumber - b.seatNumber)
                  .map((s) =>
                    `<tr><td>${s.seatNumber}</td><td>${s.guestName || ''}</td><td>${s.guestTicketNumber || ''}</td><td>${s.guestTier || ''}</td><td>${s.guestMeal || ''}</td><td>${s.guestChapter || ''}</td><td>${s.guestFamily || ''}</td><td>${s.guestVip ? 'VIP' : ''}</td></tr>`
                  )
                  .join('');
                const html = `<!DOCTYPE html><html><head><title>Table ${table.tableNumber} — ${table.zone.name}</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>Table ${table.tableNumber} — ${table.tableName}</h1><p>${table.zone.name} Zone • ${occupiedSeats.length} guests • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                  printWindow.document.write(html);
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => printWindow.print(), 300);
                }
              };

              return (
                <div style={{ marginBottom: '1rem', borderBottom: `1px solid ${BORDER_LIGHT}`, paddingBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_DARK, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      Guest List ({occupiedSeats.length})
                    </span>
                    <button
                      style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer', color: '#333' }}
                      onClick={handlePrintTable}
                      aria-label={`Print guest list for table ${table.tableNumber}`}
                    >
                      🖨️ Print Table
                    </button>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                    <thead>
                      <tr>
                        {['Seat', 'Name', 'Ticket', 'Tier', 'Meal', 'Chapter', 'Family', 'VIP'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', padding: '3px 5px', borderBottom: '1px solid #ddd', fontWeight: 600, color: '#555' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {occupiedSeats
                        .sort((a, b) => a.seatNumber - b.seatNumber)
                        .map((s) => (
                          <tr key={s.id}>
                            <td style={{ padding: '3px 5px' }}>{s.seatNumber}</td>
                            <td style={{ padding: '3px 5px', fontWeight: 500 }}>{s.guestName || '—'}</td>
                            <td style={{ padding: '3px 5px', fontFamily: 'monospace', fontSize: '0.68rem' }}>{s.guestTicketNumber || '—'}</td>
                            <td style={{ padding: '3px 5px' }}>{s.guestTier || '—'}</td>
                            <td style={{ padding: '3px 5px' }}>{s.guestMeal || '—'}</td>
                            <td style={{ padding: '3px 5px' }}>{s.guestChapter || '—'}</td>
                            <td style={{ padding: '3px 5px' }}>{s.guestFamily || '—'}</td>
                            <td style={{ padding: '3px 5px' }}>{s.guestVip ? <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#e65100' }}>VIP</span> : ''}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            <div style={styles.seatsList} aria-label="Seats list">
              {table.seats
                .sort((a, b) => a.seatNumber - b.seatNumber)
                .map((seat) => (
                  <SeatCard
                    key={seat.id}
                    seat={seat}
                    onAssign={onAssignSeat}
                    onUnassign={handleUnassign}
                    onBlock={onBlockSeat}
                    onHold={onHoldSeat}
                    onRelease={handleReleaseSeat}
                  />
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
